import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { confirmable, createConfirmation } from 'react-confirm';
import { useTranslation } from 'react-i18next';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';

import { PSButton } from '@/components/ui/button';

type ButtonVariant =
  | 'primary'
  | 'info'
  | 'danger'
  | 'outlineDanger'
  | 'disabled'
  | 'warning'
  | 'outlineWarning'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'gray'
  | 'none';

interface ConfirmDialogOptions {
  description?: string;
  imageUrl?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonStyle?: ButtonVariant;
  cancelButtonStyle?: ButtonVariant;
}

const ConfirmationDialog = ({
  show = false,
  proceed = (_value: boolean) => undefined,
  confirmation = '',
  confirmButtonStyle,
  cancelButtonStyle: cancelButtonStyleProp,
  confirmButtonText,
  cancelButtonText,
  ...options
}: any) => {
  const { t } = useTranslation();

  const renderIcon = () => {
    if ('imageUrl' in options && options.imageUrl) {
      return <img src={options.imageUrl} alt="confirmation-icon" />;
    }

    return (
      <div
        className={`tw-w-10 tw-h-10 tw-p-2.5  tw-rounded-3xl tw-justify-center tw-items-center tw-inline-flex ${
          ['danger', 'outlineDanger'].includes(confirmButtonStyle) ? 'tw-bg-red-100' : 'tw-bg-amber-100'
        }`}
      >
        <div className="tw-w-5 tw-h-5 tw-relative tw-flex-col tw-justify-start tw-items-start tw-flex">
          <BsFillExclamationTriangleFill
            size={20}
            className={['danger', 'outlineDanger'].includes(confirmButtonStyle) ? 'tw-text-red-600' : 'tw-text-amber-500'}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      contentClassName="tw-bg-white tw-rounded-2xl tw-shadow tw-flex-col tw-justify-start tw-items-center tw-inline-flex"
      show={show}
      onHide={() => proceed(false)}
      backdrop="static"
      centered
      size="sm"
    >
      <Modal.Body className="tw-flex tw-flex-col tw-gap-y-4">
        <div className="tw-self-stretch tw-pt-5 tw-pb-4 tw-flex-col tw-justify-start tw-items-center tw-gap-3 tw-flex">
          {renderIcon()}
          <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-center tw-gap-2 tw-flex">
            <div className="tw-self-stretch tw-text-center tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">{confirmation}</div>
            {'description' in options && options.description && (
              <div className="tw-self-stretch tw-text-center tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {options.description}
              </div>
            )}
          </div>
        </div>
        <div className="tw-self-stretch tw-px-4 tw-py-3 tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
          <PSButton
            id="btn-confirm-cancel"
            onClick={() => proceed(false)}
            variant={cancelButtonStyleProp || 'primary'}
            title={t(cancelButtonText || 'ConfirmTreeDeleteModal/key1')}
          />
          <PSButton
            id="btn-confirm-proceed"
            variant={confirmButtonStyle || 'danger'}
            onClick={() => proceed(true)}
            title={t(confirmButtonText || 'ConfirmTreeDeleteModal/key2')}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

ConfirmationDialog.propTypes = {
  confirmation: PropTypes.string,
  confirmButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  confirmButtonStyle: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'link']),
  cancelButtonStyle: PropTypes.oneOf(['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'link']),
  show: PropTypes.bool,
  proceed: PropTypes.func,
};

const confirmDialog = createConfirmation(confirmable(ConfirmationDialog));

/**
 * General-purpose confirmation dialog with modern UI
 * @param message - The confirmation message to display
 * @param options - Optional configuration
 * @param options.description - Description text to show below the confirmation message
 * @param options.imageUrl - Custom image URL to display instead of the default icon (legacy)
 * @param options.confirmButtonText - Translation key for the confirm button text (default: 'ConfirmTreeDeleteModal/key2')
 * @param options.cancelButtonText - Translation key for the cancel button text (default: 'ConfirmTreeDeleteModal/key1')
 * @param options.confirmButtonStyle - Button variant for the confirm button (default: 'danger')
 * @param options.cancelButtonStyle - Button variant for the cancel button (default: 'primary')
 * @returns Promise that resolves to true if confirmed, false if canceled
 */
export const confirmWithDialog = (message: string, options: ConfirmDialogOptions = {}) => {
  return confirmDialog(Object.assign({ confirmation: message }, options));
};
